import type { DateFormat, Dayjs, TimeFormat } from 'Utils/dateTime';
import {
  convertDateFormatToDayjs,
  convertTimeFormatToDayjs,
  createDayjsInstance,
  dayjs
} from 'Utils/dateTime';

import type { CourseDateEntry } from '../forms/shared/types';

export interface ValidationResult {
  isValid: boolean;
  error?: string;
}

export const validateCourseDateEntry = (
  courseDate: CourseDateEntry,
  index: number,
  dateFormat: DateFormat,
  timeFormat: TimeFormat,
  t: (key: string) => string
): ValidationResult => {
  if (
    !courseDate.startDate ||
    !courseDate.endDate ||
    !courseDate.startTime ||
    !courseDate.endTime
  ) {
    return {
      isValid: false,
      error: `Session ${index + 1}: ${t('courseForm.validation.sessionStartEndRequired')}`
    };
  }

  const dayjsDateFormat = convertDateFormatToDayjs(dateFormat);
  const dayjsTimeFormat = convertTimeFormatToDayjs(timeFormat);
  const combinedFormat = `${dayjsDateFormat}T${dayjsTimeFormat}`;

  let start: Dayjs;
  let end: Dayjs;

  try {
    start = dayjs(`${courseDate.startDate}T${courseDate.startTime}`, combinedFormat);
    end = dayjs(`${courseDate.endDate}T${courseDate.endTime}`, combinedFormat);
  } catch {
    return {
      isValid: false,
      error: `Session ${index + 1}: ${t('courseForm.validation.sessionInvalidStartDateTime')}`
    };
  }

  if (!start.isValid()) {
    return {
      isValid: false,
      error: `Session ${index + 1}: ${t('courseForm.validation.sessionInvalidStartDateTime')}`
    };
  }

  if (!end.isValid()) {
    return {
      isValid: false,
      error: `Session ${index + 1}: ${t('courseForm.validation.sessionInvalidEndDateTime')}`
    };
  }

  if (start.isSame(end)) {
    return {
      isValid: false,
      error: `Session ${index + 1}: ${t('courseForm.validation.sessionSameStartEnd')}`
    };
  }

  if (start.isAfter(end)) {
    return {
      isValid: false,
      error: `Session ${index + 1}: ${t('courseForm.validation.sessionStartAfterEnd')}`
    };
  }

  const currentTime = createDayjsInstance();
  const minimumDate = currentTime.subtract(1, 'year');
  const maximumDate = currentTime.add(5, 'year');

  if (start.isBefore(minimumDate)) {
    return {
      isValid: false,
      error: `Session ${index + 1}: ${t('courseForm.validation.sessionPastLimit')}`
    };
  }

  if (start.isAfter(maximumDate)) {
    return {
      isValid: false,
      error: `Session ${index + 1}: ${t('courseForm.validation.sessionFutureLimit')}`
    };
  }

  return { isValid: true };
};

export const validateCourseDatesOrdering = (
  courseDates: CourseDateEntry[],
  courseTimezone: string,
  dateFormat: DateFormat,
  timeFormat: TimeFormat,
  t: (key: string) => string
): ValidationResult => {
  if (courseDates.length <= 1) {
    return { isValid: true };
  }

  const dayjsDateFormat = convertDateFormatToDayjs(dateFormat);
  const dayjsTimeFormat = convertTimeFormatToDayjs(timeFormat);
  const combinedFormat = `${dayjsDateFormat}T${dayjsTimeFormat}`;

  for (let i = 0; i < courseDates.length - 1; i++) {
    let currentStart: Dayjs;
    let nextStart: Dayjs;

    try {
      currentStart = dayjs(
        `${courseDates[i].startDate}T${courseDates[i].startTime}`,
        combinedFormat
      ).tz(courseTimezone);
      nextStart = dayjs(
        `${courseDates[i + 1].startDate}T${courseDates[i + 1].startTime}`,
        combinedFormat
      ).tz(courseTimezone);
    } catch {
      continue;
    }

    if (!currentStart.isValid() || !nextStart.isValid()) {
      continue;
    }

    if (currentStart.isAfter(nextStart)) {
      return {
        isValid: false,
        error: t('courseForm.validation.datesMustBeInOrder')
      };
    }
  }

  for (let i = 0; i < courseDates.length - 1; i++) {
    let currentEnd: Dayjs;
    let nextStart: Dayjs;

    try {
      currentEnd = dayjs(`${courseDates[i].endDate}T${courseDates[i].endTime}`, combinedFormat).tz(
        courseTimezone
      );
      nextStart = dayjs(
        `${courseDates[i + 1].startDate}T${courseDates[i + 1].startTime}`,
        combinedFormat
      ).tz(courseTimezone);
    } catch {
      continue;
    }

    if (!currentEnd.isValid() || !nextStart.isValid()) {
      continue;
    }

    if (currentEnd.isAfter(nextStart)) {
      return {
        isValid: false,
        error: t('courseForm.validation.datesCannotOverlap')
      };
    }
  }

  return { isValid: true };
};

export const validateAllCourseDates = (
  dates: CourseDateEntry[],
  timezone: string,
  dateFormat: DateFormat,
  timeFormat: TimeFormat,
  t: (key: string) => string
): Record<string, string> => {
  const errors: Record<string, string> = {};

  dates.forEach((date, index) => {
    const validation = validateCourseDateEntry(date, index, dateFormat, timeFormat, t);
    if (!validation.isValid) {
      errors[date.id] = validation.error ?? t('courseForm.validation.invalidDateFormat');
    }
  });

  const orderingValidation = validateCourseDatesOrdering(
    dates,
    timezone,
    dateFormat,
    timeFormat,
    t
  );

  if (!orderingValidation.isValid) {
    dates.forEach(date => {
      if (!errors[date.id]) {
        errors[date.id] =
          orderingValidation.error ?? t('courseForm.validation.sessionInvalidOrdering');
      }
    });
  }

  return errors;
};

export const validateFormData = (
  formData: {
    expectedParticipants?: number;
    maxParticipants?: number;
  },
  t: (key: string) => string
): Record<string, string> => {
  const validationErrors: Record<string, string> = {};

  if (formData.maxParticipants === undefined || formData.maxParticipants === null) {
    validationErrors.maxParticipants = t('courseForm.validation.maxParticipantsRequired');
  } else if (formData.maxParticipants > 1000) {
    validationErrors.maxParticipants = t('courseForm.validation.maxParticipantsTooHigh');
  }

  if (
    formData.expectedParticipants !== undefined &&
    formData.expectedParticipants !== null &&
    formData.maxParticipants !== undefined &&
    formData.maxParticipants !== null &&
    formData.expectedParticipants > formData.maxParticipants
  ) {
    validationErrors.expectedParticipants = t(
      'courseForm.validation.expectedParticipantsExceedsMax'
    );
  }

  return validationErrors;
};
